import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import type { WorkItem } from "../lib/types";
import { Tile, SectionHeader, Pill } from "../components/primitives";

// Today planner: KPI row + "My day" list of items due today, ordered by due time. Kanban-adjacent
// but personal — the first thing a user sees. KPIs are derived from the same list/board data.
export function TodayView() {
  const { data: tasks } = useQuery({ queryKey: ["today", "tasks"], queryFn: () => api.listWorkItems({ kind: "task", limit: 100 }) });
  const { data: tickets } = useQuery({ queryKey: ["today", "tickets"], queryFn: () => api.listWorkItems({ kind: "ticket", limit: 100 }) });

  const allItems = [...(tasks?.items ?? []), ...(tickets?.items ?? [])];
  const open = allItems.filter((i) => !i.completed_at);
  const dueToday = open.filter((i) => i.due_at && isToday(i.due_at));
  const triage = (tickets?.items ?? []).filter((t) => t.status === "new" || t.status === "triage");
  const slaAtRisk = allItems.filter(
    (i) => i.sla_breached_at || (i.sla_due_at && new Date(i.sla_due_at).getTime() - Date.now() < 3_600_000 && !i.completed_at),
  );

  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr 1fr 1fr", gap: 14 }}>
        <Kpi label="Open work items" value={open.length} sub="across tasks & tickets" gold />
        <Kpi label="Due today" value={dueToday.length} sub={`${dueToday.filter((i) => i.priority === "high" || i.priority === "urgent").length} high priority`} />
        <Kpi label="Tickets in triage" value={triage.length} sub="awaiting assignment" />
        <Kpi label="SLA at risk" value={slaAtRisk.length} sub="within the hour" danger />
      </div>

      <Tile>
        <SectionHeader title="My day">
          <Pill className="pill-brown">{new Date().toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" })}</Pill>
        </SectionHeader>
        <div style={{ height: 8 }} />
        <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {dueToday.length === 0 && <div style={{ padding: "12px 4px", color: "var(--text-3)", fontSize: 13 }}>Nothing due today. 🐝</div>}
          {dueToday
            .slice()
            .sort((a, b) => (a.due_at ?? "").localeCompare(b.due_at ?? ""))
            .map((i) => (
              <PlannerRow key={i.id} item={i} />
            ))}
        </div>
      </Tile>
    </>
  );
}

function isToday(iso: string): boolean {
  const d = new Date(iso);
  const n = new Date();
  return d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth() && d.getDate() === n.getDate();
}

function Kpi({ label, value, sub, gold = false, danger = false }: { label: string; value: number; sub: string; gold?: boolean; danger?: boolean }) {
  return (
    <Tile gold={gold} className="">
      <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.7px", color: "var(--text-3)", fontWeight: 600 }}>{label}</div>
      <div
        style={{
          fontSize: 29,
          fontWeight: 700,
          letterSpacing: "-1px",
          margin: "7px 0 3px",
          ...(gold
            ? { background: "var(--gradient-gold)", WebkitBackgroundClip: "text", backgroundClip: "text", WebkitTextFillColor: "transparent" }
            : danger
              ? { color: "var(--red)" }
              : {}),
        }}
      >
        {value}
      </div>
      <div style={{ fontSize: 11.5, color: "var(--text-3)" }}>{sub}</div>
    </Tile>
  );
}

function PlannerRow({ item }: { item: WorkItem }) {
  const done = Boolean(item.completed_at);
  return (
    <Link
      to={`/item/${item.id}`}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "11px 4px",
        borderBottom: "1px solid var(--border)",
        color: "inherit",
        textDecoration: "none",
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 16,
          height: 16,
          borderRadius: 5,
          border: done ? "0" : "1.5px solid var(--text-4)",
          background: done ? "var(--gradient-gold)" : "transparent",
          display: "grid",
          placeItems: "center",
          fontSize: 9,
          color: "#1A1206",
          flex: "none",
        }}
      >
        {done ? "✓" : ""}
      </span>
      <span
        style={{
          fontSize: 13,
          fontWeight: 500,
          color: done ? "var(--text-4)" : "var(--text-1)",
          textDecoration: done ? "line-through" : "none",
        }}
      >
        {item.title}
      </span>
      <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--text-3)", fontWeight: 600 }}>
        {item.due_at ? new Date(item.due_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }) : ""}
      </span>
    </Link>
  );
}
